import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { check, signOut, sync } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import {
  actionOpenLaborTicketModal,
  actionOpenSetupActionModal,
} from 'lib/actions';
import { getSkipSetupParts } from 'lib/selectors/getSkipSetupParts';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getMachine } from 'lib/selectors/getMachine';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import Routes from 'lib/utils/routes';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { CategorizeDowntime } from './CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
} from '../styled';

/**
 * Control bar component for actively running job state.
 *
 * This component is rendered when:
 * - Operator is signed in
 * - Job is currently running
 * - Job is NOT paused
 * - System is NOT in ERP-driven mode
 *
 * Available actions:
 * - Submit Work - Submit completed work with conditional modal logic:
 *   - If embedded labor flow enabled: opens setup action modal (if setup ticket) or labor ticket modal
 *   - Otherwise: opens labor ticket modal with STOP_AND_CREATE action
 * - Switch Activity - Only shown when multiple activity types are available and job is not paused
 * - Categorize Downtime - Categorize downtime intervals
 * - Sign Out - Complex sign-out logic:
 *   - If paused: simple logout
 *   - Otherwise: navigates home, then opens setup action modal (if applicable) or labor ticket modal
 *     with STOP_AND_CREATE_OPERATOR_LOGOUT action
 *
 * @returns {JSX.Element} Control bar with submit, switch activity, downtime categorization, and sign out options
 */
const ActiveJobRunning = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const isPaused = useSelector(getIsPaused);
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const skipSetupParts = useSelector(getSkipSetupParts);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isSetupTicket = currentLaborTicket?.type?.toLowerCase() === 'setup';
  const useSetupActionModal = skipSetupParts && isSetupTicket;
  const activityTypes = useSelector(getSwitchActivityTypesOptions);
  const { openSelectActivity } = useSelectActivity();
  const requestOperatorLogout = useOperatorLogout();

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Scrollable>
        {/* Submit Work button - handles work submission with conditional modal logic */}
        <ControlBarButton
          id="job-control-bar-submit"
          onClick={() => {
            // Embedded labor flow enabled - use appropriate modal
            if (isEmbeddedLaborFlowEnabled) {
              // Setup action modal for setup tickets when skipping setup parts
              if (useSetupActionModal) {
                return dispatch(actionOpenSetupActionModal('submit'));
              }
              // Standard labor ticket modal
              return dispatch(actionOpenLaborTicketModal('submit'));
            }

            // Non-embedded flow - stop and create new labor ticket
            return dispatch(
              actionOpenLaborTicketModal(LABOR_TICKET_ACTIONS.STOP_AND_CREATE)
            );
          }}
        >
          <MobileIcon icon={check} />
          <ResponsiveText
            full={t('submitWork|full')}
            medium={t('submitWork|medium')}
            short={t('submitWork|short')}
          />
        </ControlBarButton>

        {/* Switch Activity button - only shown when multiple activity types available and not paused */}
        {!isPaused && activityTypes?.length > 1 && (
          <ControlBarButton
            id="job-control-switch-activity"
            onClick={() => {
              return openSelectActivity();
            }}
          >
            <MobileIcon icon={sync} />
            <ResponsiveText
              full={t('switchActivity|full')}
              medium={t('switchActivity|medium')}
              short={t('switchActivity|short')}
            />
          </ControlBarButton>
        )}

        {/* Downtime categorization */}
        <CategorizeDowntime history={history} machine={machine} />

        {/* Sign Out button - complex logic based on job state and configuration */}
        <ControlBarButton
          id="signout"
          onClick={() => {
            // If paused, simple logout
            if (isPaused) {
              return requestOperatorLogout();
            }
            // Navigate home first
            history.push(Routes.machineIdHomePath(machine.id));
            // Then open appropriate modal if embedded flow is enabled
            if (isEmbeddedLaborFlowEnabled && useSetupActionModal) {
              return dispatch(actionOpenSetupActionModal('signout'));
            }
            // Otherwise, open labor ticket modal with logout action
            return dispatch(
              actionOpenLaborTicketModal(
                LABOR_TICKET_ACTIONS.STOP_AND_CREATE_OPERATOR_LOGOUT
              )
            );
          }}
        >
          <MobileIcon icon={signOut} />
          <ResponsiveText
            full={t('signout|full')}
            medium={t('signout|medium')}
            short={t('signout|short')}
          />
        </ControlBarButton>
      </Scrollable>
    </ControlBarResponsive>
  );
};

export { ActiveJobRunning };
